import {
  Flex,
  Text,
  Input,
  InputGroup,
  InputLeftAddon,
  Box,
  Divider,
  FormControl,
  FormErrorMessage,
  FormLabel,
} from '@chakra-ui/react';
import { useAppDispatch, useAppSelector } from '../../../store/redux/hooks';
import { useRef, useState } from 'react';
import { CustomAlertTriangleIcon } from '../../../theme/icons/CustomAlertTriangleIcon';
import { getLocaleDisplayName } from '../../../constants/locales';
import {
  setFieldLocalisation,
  setPreviewLanguage,
  setFocusedField,
} from '../../../store/redux/slices/template.slice';
import {
  setError,
  clearError,
} from '../../../store/redux/slices/templateErrors.slice';
import { FieldSection, FieldUIState } from '../../../store/types/template';
import FieldEditIndicator from './FieldEditIndicator';
import { useFieldEditIndicator } from './hooks/useFieldEditIndicator';
import { CustomErrorIcon } from '../../../theme/icons/CustomErrorIcon';

interface LocalisedFieldProps {
  section: FieldSection;
  field: string;
  fieldData: FieldUIState;
  language: string;
  showLabel?: boolean;
  showDefaultValue?: boolean;
  sectionName?: string;
  isVisible?: boolean;
  compact?: boolean;
  disabled?: boolean;
  showWarnings?: boolean;
}

const LocalisedField = ({
  section,
  field,
  fieldData,
  language,
  showLabel = true,
  showDefaultValue = true,
  sectionName,
  isVisible = true,
  compact = false,
  disabled = false,
  showWarnings = false,
}: LocalisedFieldProps) => {
  const dispatch = useAppDispatch();
  const defaultLanguage = useAppSelector((s) => s.template.defaultLanguage);
  const [labelTouched, setLabelTouched] = useState(false);
  const [defaultValueTouched, setDefaultValueTouched] = useState(false);
  const labelRef = useRef<HTMLDivElement>(null);
  const defaultValueRef = useRef<HTMLDivElement>(null);

  const fieldIdentifier = `${section}.${field}`;
  const { activeFieldRef, isFieldBeingEdited } = useFieldEditIndicator({
    fieldRefMap: {
      [`${fieldIdentifier}.label`]: labelRef,
      [`${fieldIdentifier}.defaultValue`]: defaultValueRef,
    },
  });

  const localisationErrorPath = `fields.${section}.${field}.localisation.${language}`;
  const localisationError = useAppSelector(
    (s) => s.templateErrors[localisationErrorPath]
  );

  const validateLocalisation = (label: string, defaultValue: string) => {
    if (defaultValue.trim() && !label.trim()) {
      dispatch(
        setError({
          path: localisationErrorPath,
          message: 'Translated label is required',
        })
      );
    } else {
      dispatch(clearError(localisationErrorPath));
    }
  };

  if (!isVisible) return null;

  const loc = fieldData.localisation?.[language];
  const labelEmpty = !loc?.label;
  const dvEmpty = !loc?.defaultValue;
  const defaultBorder = compact
    ? '2px solid rgba(228,228,228,0.5)'
    : '2px solid #dddddd80';

  const untranslatedWarning = (touched: boolean, isEmpty: boolean) =>
    showWarnings && touched && isEmpty && !localisationError ? (
      <Flex align="center" gap={2} ml={2} mt={2}>
        <CustomAlertTriangleIcon boxSize="14px" />
        <Text fontSize="12px" color="#666">
          This field isn&apos;t translated. It will appear in{' '}
          {getLocaleDisplayName(defaultLanguage || 'en')}.
        </Text>
      </Flex>
    ) : null;

  return (
    <Box>
      <Divider borderColor="lightGrey" mb={4} />
      <Flex direction="column" gap={3}>
        <Flex alignItems="center" gap={2}>
          <FormLabel variant="templateBuilderLabel" m={0} fontWeight="500">
            {sectionName}
          </FormLabel>
          {fieldData.id && (
            <Text fontSize="14px" fontWeight="600">
              | {fieldData.id}
            </Text>
          )}
        </Flex>
        {showLabel && (
          <FieldEditIndicator
            fieldRef={labelRef}
            isFieldBeingEdited={isFieldBeingEdited}
            activeFieldRef={activeFieldRef}
            left="-65px"
            top="25px"
          >
            <FormControl isInvalid={!!localisationError}>
              <InputGroup size={compact ? 'sm' : 'md'}>
                <InputLeftAddon
                  height="auto"
                  bg="white"
                  color="black"
                  fontSize={compact ? '13px' : '14px'}
                  minW={compact ? '110px' : '130px'}
                  border={defaultBorder}
                  borderRight="none"
                  borderRadius={compact ? '5px 0 0 5px' : undefined}
                >
                  Label{!compact && ':'}
                  {loc?.defaultValue && !loc?.label && (
                    <span style={{ color: 'red' }}>*</span>
                  )}
                </InputLeftAddon>
                <Input
                  isDisabled={disabled}
                  value={loc?.label || ''}
                  placeholder={fieldData.label || 'Label'}
                  fontSize={compact ? '13px' : undefined}
                  border={compact ? defaultBorder : undefined}
                  borderRadius={compact ? '0 5px 5px 0' : undefined}
                  onFocus={() => {
                    dispatch(setPreviewLanguage(language));
                    dispatch(setFocusedField(`${fieldIdentifier}.label`));
                  }}
                  onBlur={() => {
                    dispatch(setPreviewLanguage(null));
                    setLabelTouched(true);
                  }}
                  onChange={(e) => {
                    dispatch(
                      setFieldLocalisation({
                        section,
                        field,
                        language,
                        label: e.target.value,
                      })
                    );
                    validateLocalisation(e.target.value, loc?.defaultValue || '');
                  }}
                />
              </InputGroup>
              {untranslatedWarning(labelTouched, labelEmpty)}
              {localisationError && (
                <FormErrorMessage
                  pl={2}
                  display="flex"
                  alignItems="center"
                  gap={1.5}
                >
                  <CustomErrorIcon />
                  {localisationError}
                </FormErrorMessage>
              )}
            </FormControl>
          </FieldEditIndicator>
        )}
        {showDefaultValue && (
          <FieldEditIndicator
            fieldRef={defaultValueRef}
            isFieldBeingEdited={isFieldBeingEdited}
            activeFieldRef={activeFieldRef}
            left="-65px"
            top="25px"
          >
            <InputGroup size={compact ? 'sm' : 'md'}>
              <InputLeftAddon
                height="auto"
                bg="white"
                color="black"
                fontSize={compact ? '13px' : '14px'}
                minW={compact ? '110px' : '130px'}
                border={defaultBorder}
                borderRight="none"
                borderRadius={compact ? '5px 0 0 5px' : undefined}
              >
                Default value{!compact && ':'}
              </InputLeftAddon>
              <Input
                isDisabled={disabled}
                value={loc?.defaultValue || ''}
                placeholder={fieldData.defaultValue || 'Default Value'}
                fontSize={compact ? '13px' : undefined}
                border={compact ? defaultBorder : undefined}
                borderRadius={compact ? '0 5px 5px 0' : undefined}
                onFocus={() => {
                  dispatch(setPreviewLanguage(language));
                  dispatch(setFocusedField(`${fieldIdentifier}.defaultValue`));
                }}
                onBlur={() => {
                  dispatch(setPreviewLanguage(null));
                  setDefaultValueTouched(true);
                }}
                onChange={(e) => {
                  dispatch(
                    setFieldLocalisation({
                      section,
                      field,
                      language,
                      defaultValue: e.target.value,
                    })
                  );
                  validateLocalisation(loc?.label || '', e.target.value);
                }}
              />
            </InputGroup>
            {untranslatedWarning(defaultValueTouched, dvEmpty)}
          </FieldEditIndicator>
        )}
      </Flex>
    </Box>
  );
};

export default LocalisedField;
